<template>
  <div class="screen-success" :class="{ 'output-one': showWelcomeToOutputOne }" id="checkout-content">
    <WelcomeToOutputOne v-if="showWelcomeToOutputOne" />
    <div v-else>
      <GiantLightningBolt class="confirmation-icon" />
      <h2 data-test="checkout-success-confirmation-header" class="highlight">Thank you!</h2>
      <div class="confirmation-text" data-test="checkout-success-confirmation-text">
        <p>You now have full access to:</p>
        <p class="product-name">{{ name }}</p>
        <ul class="confirmation-features">
          <li v-for="feature in confirmationFeatures" class="confirmation-feature" v-bind:key="feature"><Checkmark /> {{ feature }}</li>
        </ul>
      </div>
    </div>
    <template v-if="!showWelcomeToOutputOne">
      <div v-if="appReturnLink">
        <AppLink :to="appReturnLink" class="launch-app-return">Launch {{ name }} app</AppLink>
      </div>
      <div>
        <ProductDownload />
      </div>
      <p>Visit your <a :href="accountPath" target="_blank" @click="handleAccountClick">Account</a> page to make updates and manage preferences.</p>
    </template>
  </div>
</template>
<script setup lang="ts">
import WelcomeToOutputOne from '@/components/shared/screens/WelcomeToOutputOne.vue'
import GiantLightningBolt from '@/components/icons/GiantLightningBolt.vue'
import AppLink from '@/components/shared/AppLink.vue'
import ProductDownload from '@/components/shared/screens/ProductDownload.vue'
import Checkmark from '@/components/icons/Checkmark.vue'
import { onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useAmplitude } from '@outputinc/o-analytics'
import { useProductStore } from '@/store/ProductStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { usePlanStore } from '@/store/PlanStore'
import { ProductId } from '@/types'
const productStore = useProductStore()
const checkoutStore = useCheckoutStore()
const planStore = usePlanStore()

const { track, Events } = useAmplitude()
const { name, confirmationFeatures, productId } = storeToRefs(productStore)
const { selectedPlanProduct } = storeToRefs(planStore)
const { appReturnLink } = storeToRefs(checkoutStore)

const accountPath = import.meta.env.VITE_OUTPUT_ACCOUNTS_HOSTNAME

onMounted(() => {
  // Reset scroll position for iOS
  const scrollContainer = document.getElementById('checkout-content')
  if (scrollContainer) {
    scrollContainer.scrollTop = 0
  }

  // Also reset window scroll as fallback
  window.scrollTo(0, 0)

  track(Events.CHECKOUT_SUBSCRIPTION_INSTALLATION, {
    products: [selectedPlanProduct.value]
  })
})

const handleAccountClick = () => {
  track(Events.CHECKOUT_SUBSCRIPTION_INSTALLATION_ACCOUNT_LINK_CLICK, { products: [selectedPlanProduct.value] })
}

const showWelcomeToOutputOne = computed(() => productId.value === ProductId.OutputOne)
</script>

<style lang="scss" scoped>
.screen-success {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-width: $grid-breakpoint-sm;
  padding: 0 $spacer-4;
  margin: $spacer-5 0;

  &.output-one {
    max-width: 100%;
    padding: 0;
    margin: 0;
    align-items: stretch;
  }

  @media (min-height: 769px) {
    margin: $spacer-9 0 $spacer-5;
  }

  .highlight {
    font-family: $font-family-pilat-wide-heavy;
    font-size: 48px;
    line-height: 48px;
    color: var(--color-text-primary);
    margin-bottom: $spacer-2;
  }

  .subheading {
    font-size: $fs7;
    color: var(--color-text-primary);
    line-height: $fs7;
  }

  .confirmation-icon {
    margin-bottom: $spacer-4;
  }

  .confirmation-text {
    display: flex;
    flex-direction: column;
    gap: $spacer-2;
    margin-bottom: $spacer-5;

    p {
      margin: 0;
    }

    .product-name {
      color: var(--color-text-primary);
      font-weight: bold;
      margin-bottom: $spacer-4;
    }
  }

  .confirmation-features {
    display: flex;
    flex-direction: column;
    gap: $spacer-2;
    justify-content: center;
    align-items: center;
    padding: 0;
    margin: 0;

    .confirmation-feature {
      list-style-type: none;
      display: flex;
      gap: $spacer-2;
    }
  }

  .launch-app-return {
    color: var(--color-accent-primary);
  }

  a {
    text-decoration: underline;
  }
}
</style>
